import React, { useRef, useState, useEffect } from "react";
import { siteConfig } from "../data/mock";
import { Mail, Phone, MapPin, Send, Instagram } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInView(ref, threshold = 0.15) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return inView;
}

export default function ContactPage() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef);
  const formRef = useRef(null);
  const formInView = useInView(formRef);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      toast({
        title: "Message Sent!",
        description: "Thanks for reaching out. I'll get back to you within 24 hours.",
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
    setSubmitting(false);
  };

  const contactInfo = [
    { icon: Mail, label: "Email", value: siteConfig.email, href: `mailto:${siteConfig.email}` },
    { icon: Phone, label: "Phone", value: siteConfig.phone, href: `tel:${siteConfig.phone}` },
    { icon: MapPin, label: "Location", value: siteConfig.location, href: null },
  ];

  return (
    <>
      {/* Header */}
      <section ref={heroRef} className="pt-32 pb-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div
            className={`max-w-2xl transition-all duration-700 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
              Contact
            </span>
            <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl tracking-tight text-white mt-2 mb-4">
              Let's Work Together
            </h1>
            <p className="text-neutral-400 text-lg">
              Have an upcoming game, tournament, or event? Let's talk about how I can capture it.
            </p>
          </div>
        </div>
      </section>

      {/* Form & Info */}
      <section ref={formRef} className="pb-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div
              className={`lg:col-span-2 transition-all duration-700 ${
                formInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="space-y-8 mb-10">
                {contactInfo.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="text-neutral-500 text-xs uppercase tracking-wider mb-1">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="text-white text-sm hover:text-amber-500 transition-colors duration-200"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white text-sm">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div>
                <p className="text-neutral-500 text-xs uppercase tracking-wider mb-4">
                  Follow Along
                </p>
                <div className="flex gap-4">
                  <a
                    href={siteConfig.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-lg bg-[#141414] border border-[#1e1e1e] flex items-center justify-center text-neutral-400 hover:text-amber-500 hover:border-amber-500/30 transition-all duration-200"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Form */}
            <div
              className={`lg:col-span-3 transition-all duration-700 delay-200 ${
                formInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <form
                onSubmit={handleSubmit}
                className="bg-[#141414] border border-[#1e1e1e] rounded-xl p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-neutral-400 text-xs uppercase tracking-wider mb-2">
                      Name *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="bg-[#0e0e0e] border-[#262626] text-white placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-amber-500/20"
                    />
                  </div>
                  <div>
                    <label className="block text-neutral-400 text-xs uppercase tracking-wider mb-2">
                      Email *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="bg-[#0e0e0e] border-[#262626] text-white placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-neutral-400 text-xs uppercase tracking-wider mb-2">
                    Subject
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="e.g. Game Day Coverage, Team Portraits"
                    className="bg-[#0e0e0e] border-[#262626] text-white placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-amber-500/20"
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-neutral-400 text-xs uppercase tracking-wider mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell me about your event, team, or project..."
                    rows={5}
                    className="bg-[#0e0e0e] border-[#262626] text-white placeholder:text-neutral-600 focus:border-amber-500/50 focus:ring-amber-500/20 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm py-5 rounded-md group disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Send Message
                      <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
